import { useMemo } from "react";
import { Flex, Text, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { OrderTypeBadge } from "@/pages/admin/AdminStatusBadge";
import { formatINR, formatDate } from "@/shared/utils/format.utils";

const lumpsumTypeLabel = (type: string | null | undefined): string | null => {
  if (type === "P") return "LUMPSUM";
  if (type === "R") return "REDEMPTION";
  if (type === "S") return "SWITCH";
  return null;
};

const sxpInvestmentType = (sxp: any): string | null => {
  if (!sxp) return null;
  if (sxp.status === "paused") return "PAUSE";
  return sxp.sxpType ?? null; // SIP, STP, SWP, TOPUP, SPROD
};

type OrderRow = {
  id: string;
  date: string;
  amcName: string | null;
  schemeName: string;
  investmentType: string | null;
  transactionValue: number | null;
  folioNumber: string | null;
};

type Props = {
  orders: any[]; // lumpsums from getById (aliased as `orders`)
  sxps: any[];
};

export default function OrderLogsTab({ orders, sxps }: Props) {
  const theme = useMantineTheme();

  const rows: OrderRow[] = useMemo(() => {
    const lumpsumRows: OrderRow[] = orders.map((l) => ({
      id: `lumpsum:${l.id}`,
      date: l.createdAt,
      amcName: l.masterScheme?.scheme_amc_name ?? null,
      schemeName: l.masterScheme?.name ?? l.scheme ?? "—",
      investmentType: lumpsumTypeLabel(l.type),
      transactionValue: l.amount ?? null,
      folioNumber: l.folio ?? null,
    }));

    const sxpOrderRows: OrderRow[] = sxps.flatMap((sxp) => {
      const installments = sxp.orders ?? [];
      const amcName = sxp.masterScheme?.scheme_amc_name ?? null;
      const schemeName = sxp.masterScheme?.name ?? sxp.srcScheme ?? "—";
      const investmentType = sxpInvestmentType(sxp);
      return installments.map((o: any) => ({
        id: `order:${o.id}`,
        date: o.createdAt,
        amcName,
        schemeName,
        investmentType,
        transactionValue: sxp.amount ?? null,
        folioNumber: null,
      }));
    });

    return [...lumpsumRows, ...sxpOrderRows].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [orders, sxps]);

  const dash = <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;

  const columns: MRT_ColumnDef<OrderRow>[] = useMemo(
    () => [
      {
        accessorKey: "date",
        header: "Date",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatDate(cell.getValue<string>()),
      },
      {
        accessorKey: "amcName",
        header: "AMC Name",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "schemeName",
        header: "Scheme Name",
        size: 220,
        Cell: ({ cell }) => cell.getValue<string>() || dash,
      },
      {
        accessorKey: "investmentType",
        header: "Investment type",
        size: 150,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <OrderTypeBadge type={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "transactionValue",
        header: "Transaction value",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "folioNumber",
        header: "Folio Number",
        size: 150,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="md">
      <Text fw={500} fz="md">
        All transactions
      </Text>

      <MantineReactTable
        columns={columns}
        data={rows}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        initialState={{
          density: "xs",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 300px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
      />
    </Flex>
  );
}
